export const usePasteManager = (
  onInsertFile: (file: File, range: Range) => void,
  updateValue: () => void,
) => {
  /**
   * * Функция для извлечения текста с сохранением переносов строк и отступов
   * @param html
   * @returns
   */
  const extractTextWithFormatting = (html: string): string => {
    // Создаем временный контейнер для обработки HTML
    const div = document.createElement("div");
    div.innerHTML = html;

    // Функция для рекурсивной очистки элементов от атрибутов
    const cleanElement = (element: HTMLElement) => {
      if (element.nodeType === Node.COMMENT_NODE) {
        element.remove();
        return;
      }
      // Очищаем атрибуты у текущего элемента, кроме тегов br и div
      if (element.tagName.toLowerCase() !== "br" && element.tagName.toLowerCase() !== "div") {
        // Заменяем другие теги на их текстовое содержимое
        const textNode = document.createTextNode(element.textContent || "");
        element.replaceWith(textNode);
      } else {
        // Удаляем все атрибуты (включая style) у тегов <div> и <br>
        [...element.attributes].forEach((attr) => element.removeAttribute(attr.name));
      }

      // Рекурсивно обрабатываем дочерние элементы
      element.childNodes.forEach((child) => {
        if (child.nodeType === Node.ELEMENT_NODE) {
          cleanElement(child as HTMLElement);
        }
      });
    };

    // Очищаем весь контент
    cleanElement(div);
    // Возвращаем только текстовое содержимое с тегами <div> и <br>
    return cleanHtml(div.innerHTML.replace(/<!--[\s\S]*?-->/g, ""));
  };
  /**
   * * Функция для очистки HTML
   * @param html
   * @returns
   */
  const cleanHtml = (html: string): string => {
    // Используем DOMParser для преобразования строки HTML в DOM-структуру
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    // Рекурсивно удаляем пустые div, или div без особой необходимости
    function removeUnnecessaryDivs(element: HTMLElement) {
      if (element.tagName === "DIV" && !element.innerHTML.trim()) {
        element.remove();
        return;
      }
      const children = Array.from(element.children);

      children.forEach((child) => {
        if (
          child.tagName === "DIV" &&
          child.children.length === 1 &&
          child.firstElementChild?.tagName === "DIV"
        ) {
          // Заменяем лишние <div> на их детей
          element.replaceChild(child.firstElementChild, child);
          // Продолжаем обработку для вложенных элементов
          removeUnnecessaryDivs(element);
        } else {
          removeUnnecessaryDivs(child as HTMLElement);
        }
      });
    }

    // Начинаем очистку с корневого элемента <body>
    removeUnnecessaryDivs(doc.body);
    // Возвращаем очищенную HTML-строку
    return doc.body.innerHTML;
  };

  /**
   * * Вставка html в курсор
   * @param html
   * @returns
   */
  const insertHTMLAtCursor = (html: string) => {
    const selection = window.getSelection();

    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    range.deleteContents();

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    const fragment = document.createDocumentFragment();
    let node;
    while ((node = tempDiv.firstChild)) {
      fragment.appendChild(node);
    }

    range.insertNode(fragment);

    // Перемещаем курсор после вставленного контента
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);

    updateValue();
  };

  /**
   * * Вставка текста в позицию курсора
   * @param text
   * @returns
   */
  const insertTextAtCursor = (text: string) => {
    const selection = window.getSelection();

    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0); // Получаем текущее выделение (позицию курсора)
    range.deleteContents(); // Удаляем выделенное содержимое, если есть

    const textNode = document.createTextNode(text); // Создаем новый текстовый узел
    range.insertNode(textNode); // Вставляем текстовый узел в текущее выделение

    // Перемещаем курсор после вставленного текста
    range.setStartAfter(textNode);
    range.setEndAfter(textNode);
    selection.removeAllRanges();
    selection.addRange(range);

    updateValue();
  };

  const getRangeForCursorPosition = (): Range | null => {
    const selection = window.getSelection();

    // Проверяем, есть ли активное выделение и если оно в фокусе
    if (selection && selection.rangeCount > 0) {
      return selection.getRangeAt(0); // Возвращаем текущий Range
    }

    return null; // Если Range не найден
  };

  /**
   * * Событие вставки
   * @param event
   * @returns
   */
  const onPaste = async (event: ClipboardEvent, fileNames?: string[]) => {
    // Остановить стандартное поведение вставки
    event.preventDefault();

    // Получаем данные из буфера обмена
    const clipboardData = event.clipboardData;
    const items = clipboardData?.items;

    if (!items?.length) return;

    let file: File | undefined;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      // Проверяем, является ли вставляемый объект файлом
      if (item.kind === "file") {
        file = item.getAsFile();
        break;
      }
    }

    if (file) {
      const sameFileName = fileNames?.some((name) => file?.name == name);
      if (sameFileName) {
        file = renameFile(file, getUniqueFilename(file.name, fileNames));
      }

      onInsertFile(file, getRangeForCursorPosition());
      return;
    }
    const htmlData = clipboardData.getData("text/html");
    const plainText = clipboardData.getData("text/plain");

    if (htmlData) {
      insertHTMLAtCursor(extractTextWithFormatting(htmlData));
    } else {
      insertTextAtCursor(plainText);
    }
  };

  return {
    onPaste,
  };

  /**Получение уникального имени файла */
  function getUniqueFilename(filename: string, existingFiles: string[]) {
    let match = filename.match(/^(.*?)(\((\d+)\))?(\.[^.]*)?$/);
    let baseName = match?.[1];
    let existingNumber = match?.[3] ? parseInt(match[3], 10) : 0;
    let ext = match?.[4] || "";
    let counter = existingNumber ? existingNumber + 1 : 1;
    let newFilename = filename;

    while (existingFiles.includes(newFilename)) {
      newFilename = `${baseName}(${counter})${ext}`;
      counter++;
    }

    return newFilename;
  }

  /**Получение переименованного файла */
  function renameFile(originalFile: File, newName: string) {
    return new File([originalFile], newName, {
      type: originalFile.type,
      lastModified: originalFile.lastModified,
    });
  }
};
